import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { Insight } from "@/components/Insight";
import { Keyboard } from "@/components/Keyboard";
import {
  Tip, KeyIdea, Pull, Steps, Step, Vocab, Term, NextUp,
  KeyHighlights, Highlight,
} from "@/components/Callouts";

export default function PianoFoundation2() {
  const { lesson, course, accent } = findLesson("piano-foundation", "2");
  return (
    <LessonShell
      course={course}
      courseSlug="piano-foundation"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <KeyHighlights>
        <Highlight>Five black keys per octave — they're music's <strong>sharps</strong> and <strong>flats</strong>. A sharp is also a flat; same key, two names.</Highlight>
        <Highlight>Sharp = "after" or "right of"; flat = "before" or "left of". A guitarist would call the sharp "upper fret"; a pianist calls it "the next step up".</Highlight>
        <Highlight>The same five keys live inside the twins and triplets you already know — twin 1, twin 2, triplet 1, triplet 2, triplet 3.</Highlight>
        <Highlight>The two names for one black key are called <strong>enharmonics</strong>: C♯ and D♭ are the exact same pitch — nothing brighter or darker about either name.</Highlight>
        <Highlight>Twelve notes total in music — seven naturals (white) plus five sharps/flats (black). Twelve shapes to learn, and the rest is recombination.</Highlight>
      </KeyHighlights>

      <Insight eyebrow="The five black notes" title="Sharps and flats — same five keys, two names each" accent={accent.hex}>
        <p>
          The black keys on the piano are music's <strong>sharps and flats</strong>. There are
          five of them per octave. And the first thing to internalise — insist on
          this — is that <em>a sharp is also a flat</em>. Same key, two names. We call them{" "}
          <strong>enharmonics</strong> of each other.
        </p>

        <Vocab>
          <Term name="Sharp (♯)" def="the note immediately to the right (a step up). 'After' the natural." />
          <Term name="Flat (♭)" def="the note immediately to the left (a step down). 'Before' the natural." />
          <Term name="Enharmonic" def="two different names for the exact same pitch. C♯ and D♭ are enharmonic." />
          <Term name="Natural" def="any of the seven white notes — A, B, C, D, E, F, G." />
        </Vocab>

        <KeyIdea>
          Nothing is happier or sadder, brighter or darker about the two names. They're the
          same pitch. Someone just ended up calling it C♯ in one context and D♭ in another to
          fit the music-theory syntax cleanly.
        </KeyIdea>
      </Insight>

      <Insight eyebrow="Why two names" title="The piano's zigzag layout" accent={accent.hex}>
        <p>
          The piano lays every pitch out in one straight line, low to high — which is why
          it's the easiest instrument to <em>see</em> theory on. What zigzags is the pattern
          of <em>colours</em>: white and black weaving in twos and threes. And unlike a
          guitar string, you can't bend a piano note or slide it. What you see is what you
          get. Find the note, play it.
        </p>
        <p>
          That zigzag is also why the same black key needs two names. A guitarist would think
          of the sharp as the "upper fret"; a pianist thinks of the same key as the next step
          up from a natural. Both terms are right — and music theory uses whichever fits the
          surrounding key signature best.
        </p>
      </Insight>

      <Insight eyebrow="Naming all five" title="Each black key, by twin or triplet" accent={accent.hex}>
        <p>Walking through the five blacks in The order:</p>

        <Steps>
          <Step><strong>Twin 1</strong> · <strong>C♯ / D♭</strong> — after C, before D.</Step>
          <Step><strong>Twin 2</strong> · <strong>D♯ / E♭</strong> — after D, before E.</Step>
          <Step><strong>Triplet 1</strong> (leftmost) · <strong>F♯ / G♭</strong> — after F, before G.</Step>
          <Step><strong>Triplet 2</strong> (middle) · <strong>G♯ / A♭</strong> — after G, before A.</Step>
          <Step><strong>Triplet 3</strong> (rightmost) · <strong>A♯ / B♭</strong> — after A, before B.</Step>
        </Steps>

        <Tip>
          The trick is to read both names at the same time. Don't memorise the sharps in one
          pass and the flats in another — pair them. C♯/D♭ is one note with two faces; treat
          it as one mental unit, two labels.
        </Tip>
      </Insight>

      <Insight title="The map · five blacks across one octave" accent={accent.hex}>
        <Keyboard
          startOctave={3}
          octaves={2}
          highlights={[
            { pitch: "C#4", color: "#a855f7", label: "C♯/D♭" },
            { pitch: "D#4", color: "#3b82f6", label: "D♯/E♭" },
            { pitch: "F#4", color: "#22c55e", label: "F♯/G♭" },
            { pitch: "G#4", color: "#f59e0b", label: "G♯/A♭" },
            { pitch: "A#4", color: "#ec4899", label: "A♯/B♭" },
          ]}
          height={200}
        />
        <p className="prose-nsm text-sm">
          The two purple/blue keys make up <strong>twin 1</strong> and{" "}
          <strong>twin 2</strong>. The three coloured keys to the right form your{" "}
          <strong>triplet</strong> — left, middle, right. Each key plays both names; the same
          pitch, just two pieces of music-theory paperwork.
        </p>
      </Insight>

      <Insight eyebrow="The full count" title="Seven naturals plus five sharps/flats — twelve in all" accent={accent.hex}>
        <Pull>
          Another way to look at it is there are seven unique white notes on the piano
          and five unique black notes on the piano. There are just 12 shapes to learn.
        </Pull>

        <p>
          Seven white naturals, five black sharps/flats — twelve unique pitches per octave.
          That's the entire alphabet of Western music. Once you can name any of those twelve
          on demand, the doors open: melody, harmony, intervals, chords, scales — they're all
          built from this exact set.
        </p>
      </Insight>

      <NextUp
        to="/lesson/piano-foundation-3"
        label="Lesson 3 · Note Recognition Exercise"
        why="Knowing the names is one thing. Naming any note in under a second, on instinct, is the next thing — and that's what the recognition drill builds."
      />
    </LessonShell>
  );
}
